<template>
  <div class="technical-information technical-information-content" v-if="userRoleGroup">
    <div class="scan-data-list" v-if="isProjectAutomatic">
      <wombat-table
        class="scan-data-table"
        :items="dataStreamForScanData"
        :columns="tableColumns"
        :resize="false"
        :loadingItems="false"
        :columnsReorder="false"
        :itemHeight="58"
        :noResultsMessage="$t('noResultsFound')"
      >
      <component
        slot="row-cell"
        slot-scope="rowCell"
        class="row-cell"
        :is-download-disabled='isDownloadDisabled'
        :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
        :item="rowCell.item"
        :column="rowCell.column"
      />
      </wombat-table>
    </div>
    <div v-else>
      <div class="technical-information-content-line" v-for="(res, index) in detailedProject.resources" :key="res.id">
        <div class="resource-header">
          <label class="resource-index"> {{ $t('newProject.resources.resource') }} #{{ index + 1 }}</label>
          <div class="resource-name">{{ res.name }}</div>
        </div>
        <resource-credentials
          v-if="showCreds"
          :resource="res"
          :resourceNumber="index"
          :editable="true"
          @update="updateResourceCredentials"
        />
        <div v-if="res.features.length" class="resource-features">
          <label class="resource-title"> {{ $t('project.resources.features') }}</label>
          <div class="column feature-block" v-for="feature in res.features" :key="feature.id">
            <div class="resource-data-text feature-text" :title="$pt('newProject.resources.feature', feature.name)">
              {{ $pt('newProject.resources.feature', feature.name) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { views } from '@/router/constants';
import { userRoleGroups } from '@/constants/auth';
import {
  testBoxTypes, projectStatuses, testTypes, TEST_TYPES_TLDS,
} from '@/constants/projects';
import { UPDATE_PROJECT_CREDENTIALS, LOAD_DETAILED_PROJECT } from '@/store/project/actionTypes';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import ResourceCredentials from '@/components/ProjectDetails/credentials/ResourceCredentials.vue';
import { getEmptyColumn, scanDataColumns } from '@/services/tableColumns';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import ProjectExportScanTableCell from '@/components/tableCells/ProjectScanDataTableCells/ProjectExportScanTableCell.vue';
import ProjectScanDateTableCell from '@/components/tableCells/ProjectScanDataTableCells/ProjectScanDateTableCell.vue';

export default {
  name: 'TechnicalInformation',
  components: {
    ResourceCredentials,
    WombatTable,
    ProjectExportScanTableCell,
    ProjectScanDateTableCell,
  },
  data() {
    return {
      dataStreamForScanData: [],
    };
  },
  computed: {
    ...mapGetters(['detailedProject', 'userRoleGroup']),
    isUserSP() {
      return this.userRoleGroup === userRoleGroups.SP;
    },
    showCreds() {
      return this.detailedProject.testingType === testBoxTypes.GRAY_BOX;
    },
    isProjectAutomatic() {
      return this.detailedProject.type === testTypes.AUTOMATIC;
    },
    scanDataList() {
      return this.detailedProject.scanData;
    },
    isDownloadDisabled() {
      return this.detailedProject.testCard.type === TEST_TYPES_TLDS.sitemap;
    },
    tableRowCellComponentsHash() {
      return {
        scanDate: ProjectScanDateTableCell,
        exportReport: ProjectExportScanTableCell,
        default: WombatRowDefaultCell,
      };
    },
    tableColumns() {
      const columns = [
        scanDataColumns.date,
        scanDataColumns.export,
      ].filter(column => column)
        .map(column => ({
          ...column,
          title: column.title && this.$t(column.title),
        }));
      return [getEmptyColumn(32), ...columns, getEmptyColumn(85)];
    },
  },
  async created() {
    if (this.isProjectAutomatic) {
      this.dataStreamForScanData = [];
      for (let i = 0; i < this.scanDataList.length; i += 1) {
        const scan = this.scanDataList[i];
        this.dataStreamForScanData.push({
          id: scan.id,
          date: scan.createdDate,
          projectId: this.detailedProject.id,
        });
      }
      this.dataStreamForScanData.sort((a, b) => {
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);
        if (aDate > bDate) return -1;
        return 1;
      });
    }
  },
  watch: {
    userRoleGroup() {
      if (this.isUserSP && this.detailedProject.status !== projectStatuses.IN_PROGRESS) {
        this.$router.replace({ name: views.PROJECT_GENERAL_INFORMATION });
      }
    },
  },
  methods: {
    async updateResourceCredentials(resource) {
      await this.$store.dispatch(UPDATE_PROJECT_CREDENTIALS, {
        id: this.detailedProject.id,
        credentials: resource.credentials.map(cred => ({ ...cred, projectResourceId: resource.id })),
      });

      await this.$store.dispatch(LOAD_DETAILED_PROJECT, this.detailedProject.id);
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';

.technical-information-content {
  box-sizing: border-box;
  width: rem(792);
  background-color: var(--bg-primary);
  overflow: scroll;
  padding-top: rem(14);
  overflow: auto;
  .scan-data-list {
    padding-top: rem(20);
  }

  .resource-data-text {
    color: var(--text-primary);
    font-size: rem(14);
    letter-spacing: 0;
    line-height: rem(16);

    &.resource-data-text-login-password {
      padding-left: rem(6);
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .technical-information-content-line {
    min-height: rem(32);
    margin-top: rem(11);
    border: rem(1) solid var(--border-secondary);
    border-radius: $radius-sm;
  }

  .resource-header {
    padding-top: rem(6);
    padding-bottom: rem(11);
    border-bottom: solid rem(1) var(--border-secondary);
  }

  .resource-features {
    width: rem(676);
    padding-top: rem(8);
    padding-bottom: rem(8);
  }

  .feature-block {
    vertical-align: top;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    width: rem(195);
  }

  .feature-text {
    margin-bottom: rem(10);
    padding-left: rem(16);
  }

  .resource-name {
    color: var(--text-primary);
    font-size: rem(14);
    font-weight: bold;
    letter-spacing: 0;
    line-height: rem(16);
    padding-left: rem(16);
  }

  .resource-title {
    display: block;
    color: var(--text-tertiary);
    font-size: rem(14);
    letter-spacing: 0;
    line-height: rem(16);
    text-transform: capitalize;
    padding-left: rem(16);
    margin-bottom: rem(15);
  }

  .resource-index {
    padding-left: rem(16);
    color: var(--text-tertiary);
    font-size: rem(10);
    letter-spacing: rem(0.8);
    line-height: rem(10);
    text-transform: uppercase;
  }
}

[data-theme="dark"] .technical-information-content {
  background-color: var(--bg-secondary);
}
</style>
